import type { SkillListItem } from "@/lib/skills/types";

/** A keyword→skills rule. First matching bucket wins; order is significant. */
interface AtlasBucket {
  /** Lowercased-query regex. */
  match: RegExp;
  /** Skill slugs to surface, in priority order. */
  slugs: string[];
  /** Transparent "here's why" prose. Must not name a skill that may be filtered out. */
  reason: string;
}

interface AtlasResult {
  reason: string;
  skills: SkillListItem[];
}

/** Suggestion chips shown above the input (mirror the reference prompts). */
export const ATLAS_CHIPS: readonly string[] = [
  "Starting a new app",
  "Make it secure before launch",
  "Fix accessibility",
  "Adding an AI chat feature",
];

/**
 * Ordered rules. Specific buckets (security, a11y, ai) precede the broad
 * "new build" / "ui" buckets so e.g. "new secure app" routes to security.
 */
const ATLAS_BUCKETS: readonly AtlasBucket[] = [
  {
    match: /secur|launch|safe|production|deploy|vuln/,
    slugs: ["secrets-scanner", "dep-cve-audit", "sql-injection-guard"],
    reason:
      "Shipping safely is mostly catching secrets, vulnerable deps, and injection. These three are all Vetted.",
  },
  {
    match: /access|a11y|wcag|contrast/,
    slugs: ["a11y-linter", "responsive-auditor"],
    reason: "For accessibility and cross-device polish, start with these two — both highly rated.",
  },
  {
    match: /\bai\b|llm|chat|prompt|agent/,
    slugs: ["secrets-scanner", "sql-injection-guard"],
    reason:
      "AI features add a new attack surface — the prompts and the data they touch. Start by locking down secrets and validating untrusted input.",
  },
  {
    match: /new|\bstart|scaffold|kick|greenfield|begin/,
    slugs: ["prd-to-scaffold", "design-token-extractor", "api-mock-server"],
    reason:
      "For a fresh build: scaffold from the PRD, pull tokens from the brand, and mock the API so the frontend isn't blocked.",
  },
  {
    match: /test|coverage|refactor/,
    slugs: ["test-suite-generator"],
    reason: "Lock behavior with generated tests before you refactor.",
  },
  {
    match: /\bui\b|\bux\b|design|component|storybook/,
    slugs: ["a11y-linter", "component-storybook", "design-token-extractor"],
    reason: "For UI work, these cover accessibility, docs, and theming.",
  },
];

const FALLBACK: Omit<AtlasBucket, "match"> = {
  slugs: ["prd-to-scaffold", "secrets-scanner", "a11y-linter"],
  reason:
    "Here are three popular, Vetted skills to start with — tell me more about your task to narrow it down.",
};

/**
 * Deterministic, rule-based finder. Returns the first matching bucket's reason
 * plus its skills resolved against `skills` (published only, deduped, max 3,
 * bucket order preserved). Empty/whitespace query → no result.
 */
export function answer(query: string, skills: SkillListItem[]): AtlasResult {
  const q = query.trim().toLowerCase();
  if (!q) return { reason: "", skills: [] };

  const bucket = ATLAS_BUCKETS.find((b) => b.match.test(q)) ?? FALLBACK;
  const bySlug = new Map(skills.map((s) => [s.slug, s]));
  const seen = new Set<string>();
  const picks: SkillListItem[] = [];
  for (const slug of bucket.slugs) {
    const skill = bySlug.get(slug);
    if (!skill?.published || seen.has(slug)) continue;
    seen.add(slug);
    picks.push(skill);
    if (picks.length === 3) break;
  }
  return { reason: bucket.reason, skills: picks };
}
